import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { defaultContent } from './blockTypes';
import AddBlockPanel from './AddBlockPanel';
import BlockList from './BlockList';
import PageList from './PageList';
import MenuEditor from './MenuEditor';
import BlockTypesConfig from './BlockTypesConfig';
import AppLoader from 'components/general/AppLoader';
import { canManagePageBuilder } from 'utils/acl';
import './pagebuilder.css';

const API_BASE = process.env.REACT_APP_URL || 'http://localhost:3501';

export default function PageBuilder() {
  const { user: authUser } = useSelector((x) => x.auth);
  const canManage = canManagePageBuilder(authUser);

  const emitPageBuilderUpdated = () => {
    window.dispatchEvent(new CustomEvent('tm-pagebuilder-updated', { detail: { ts: Date.now() } }));
  };

  const [pages, setPages] = useState([]);
  const [menu, setMenu] = useState([]);
  const [menuBackgroundColor, setMenuBackgroundColor] = useState('#ffffff');
  const [menuButtonColor, setMenuButtonColor] = useState('#004165');
  const [menuTextColor, setMenuTextColor] = useState('#111111');
  const [activePageId, setActivePageId] = useState(null);
  const [activeTab, setActiveTab] = useState('pages');
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [enabledBlockTypes, setEnabledBlockTypes] = useState(new Set());
  const [expandedBlockId, setExpandedBlockId] = useState(null);
  const addPanelBottomRef = useRef(null);

  // ── Get auth token (for use in fetch functions) ──────────────────
  const token = authUser?.token;

  // ── Memoized fetch functions ────────────────────────────────────
  const fetchPages = useCallback(async () => {
    if (!canManage || !token) return;

    try {
      const res = await fetch(`${API_BASE}/pagebuilder/pages`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        }
      });
      if (!res.ok) throw new Error('Failed to fetch pages');
      const data = await res.json();
      setPages(data);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching pages:', err);
    } finally {
      setLoading(false);
    }
  }, [canManage, token]);

  const fetchMenu = useCallback(async () => {
    if (!canManage || !token) return;

    try {
      const res = await fetch(`${API_BASE}/pagebuilder/menu`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        }
      });
      if (!res.ok) throw new Error('Failed to fetch menu');
      const data = await res.json();
      setMenu(data.items || []);
      setMenuBackgroundColor(data.menuBackgroundColor || '#ffffff');
      setMenuButtonColor(data.menuButtonColor || '#004165');
      setMenuTextColor(data.menuTextColor || '#111111');
    } catch (err) {
      console.error('Error fetching menu:', err);
    }
  }, [canManage, token]);

  // ── Fetch pages and menu on mount ────────────────────────────────
  useEffect(() => {
    fetchPages();
    fetchMenu();
  }, [fetchPages, fetchMenu]);

  useEffect(() => {
    if (!activePageId) return;
    if (!pages.some((page) => page.id === activePageId)) {
      setActivePageId(null);
    }
  }, [activePageId, pages]);

  useEffect(() => {
    if (!showAddPanel) return;
    window.requestAnimationFrame(() => {
      addPanelBottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    });
  }, [showAddPanel]);

  // ── Auth check (after all hooks) ────────────────────────────────
  if (!canManage) {
    return null;
  }

  const authHeaders = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };

  const effectiveActivePageId = activePageId || null;
  const activePage = pages.find((p) => p.id === effectiveActivePageId) || null;

  // ── Page CRUD ────────────────────────────────────────────────────
  const addPage = async ({ title, icon = '' }) => {
    try {
      const res = await fetch(`${API_BASE}/pagebuilder/pages`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ title, icon })
      });
      if (!res.ok) throw new Error('Failed to create page');
      const page = await res.json();
      setPages((prev) => [...prev, page]);
      emitPageBuilderUpdated();
    } catch (err) {
      setError(err.message);
      console.error('Error adding page:', err);
    }
  };

  const renamePage = async (id, { title, icon = '' }) => {
    try {
      const res = await fetch(`${API_BASE}/pagebuilder/pages/${id}`, {
        method: 'PATCH',
        headers: authHeaders,
        body: JSON.stringify({ title, icon })
      });
      if (!res.ok) throw new Error('Failed to update page');
      const updatedPage = await res.json();
      setPages((prev) => prev.map((p) => (p.id === id ? updatedPage : p)));
      setMenu((prev) => prev.map((item) => (item.pageId === id ? { ...item, label: title, icon } : item)));
      emitPageBuilderUpdated();
    } catch (err) {
      setError(err.message);
      console.error('Error renaming page:', err);
    }
  };

  const removePage = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/pagebuilder/pages/${id}`, {
        method: 'DELETE',
        headers: authHeaders
      });
      if (!res.ok) throw new Error('Failed to delete page');
      setPages((prev) => prev.filter((p) => p.id !== id));
      setMenu((prev) => prev.filter((item) => item.pageId !== id));
      if (effectiveActivePageId === id) setActivePageId(null);
      emitPageBuilderUpdated();
    } catch (err) {
      setError(err.message);
      console.error('Error removing page:', err);
    }
  };

  // ── Block CRUD (operates on active page's blocks) ────────────────
  const addBlock = async (type) => {
    if (!effectiveActivePageId) return;
    try {
      const res = await fetch(`${API_BASE}/pagebuilder/pages/${effectiveActivePageId}/blocks`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ type, content: defaultContent(type), isVisible: true })
      });
      if (!res.ok) throw new Error('Failed to add block');
      const block = await res.json();
      setPages((prev) => prev.map((p) => {
        if (p.id === effectiveActivePageId) {
          return { ...p, blocks: [...p.blocks, block] };
        }
        return p;
      }));
      setExpandedBlockId(block.id);
      setShowAddPanel(false);
      emitPageBuilderUpdated();
    } catch (err) {
      setError(err.message);
      console.error('Error adding block:', err);
    }
  };

  const updateBlock = async (blockId, updates) => {
    if (!effectiveActivePageId) return;
    try {
      const res = await fetch(`${API_BASE}/pagebuilder/pages/${effectiveActivePageId}/blocks/${blockId}`, {
        method: 'PATCH',
        headers: authHeaders,
        body: JSON.stringify(updates)
      });
      if (!res.ok) throw new Error('Failed to update block');
      const updatedBlock = await res.json();
      setPages((prev) => prev.map((p) => {
        if (p.id === effectiveActivePageId) {
          return {
            ...p,
            blocks: p.blocks.map((b) => (b.id === blockId ? updatedBlock : b))
          };
        }
        return p;
      }));
      emitPageBuilderUpdated();
    } catch (err) {
      setError(err.message);
      console.error('Error updating block:', err);
    }
  };

  const toggleBlockVisibility = async (blockId, isVisible) => {
    await updateBlock(blockId, { isVisible });
  };

  const removeBlock = async (blockId) => {
    if (!effectiveActivePageId) return;
    try {
      const res = await fetch(`${API_BASE}/pagebuilder/pages/${effectiveActivePageId}/blocks/${blockId}`, {
        method: 'DELETE',
        headers: authHeaders
      });
      if (!res.ok) throw new Error('Failed to remove block');
      setPages((prev) => prev.map((p) => {
        if (p.id === effectiveActivePageId) {
          return {
            ...p,
            blocks: p.blocks.filter((b) => b.id !== blockId)
          };
        }
        return p;
      }));
      emitPageBuilderUpdated();
    } catch (err) {
      setError(err.message);
      console.error('Error removing block:', err);
    }
  };

  const moveBlock = async (blockId, direction) => {
    if (!effectiveActivePageId) return;
    try {
      const res = await fetch(
        `${API_BASE}/pagebuilder/pages/${effectiveActivePageId}/blocks/${blockId}/reorder`,
        {
          method: 'PATCH',
          headers: authHeaders,
          body: JSON.stringify({ direction: direction === 1 ? 'down' : 'up' })
        }
      );
      if (!res.ok) throw new Error('Failed to move block');
      const { blocks } = await res.json();
      setPages((prev) => prev.map((p) => (p.id === effectiveActivePageId ? { ...p, blocks } : p)));
      emitPageBuilderUpdated();
    } catch (err) {
      setError(err.message);
      console.error('Error moving block:', err);
    }
  };

  // ── Menu CRUD ────────────────────────────────────────────────────
  const addToMenu = async (pageId, parentPageId = null) => {
    const page = pages.find((p) => p.id === pageId);
    if (!page || menu.some((item) => item.pageId === pageId)) return;
    try {
      const res = await fetch(`${API_BASE}/pagebuilder/menu/items`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ pageId, label: page.title, icon: page.icon || '', parentPageId })
      });
      if (!res.ok) throw new Error('Failed to add menu item');
      const { items } = await res.json();
      setMenu(items);
      fetchMenu();
      emitPageBuilderUpdated();
    } catch (err) {
      setError(err.message);
      console.error('Error adding menu item:', err);
    }
  };

  const removeFromMenu = async (pageId) => {
    try {
      const res = await fetch(`${API_BASE}/pagebuilder/menu/items/${pageId}`, {
        method: 'DELETE',
        headers: authHeaders
      });
      if (!res.ok) throw new Error('Failed to remove menu item');
      const menuData = await res.json();
      const items = menuData.items || [];
      setMenu(items);
      setMenuBackgroundColor(menuData.menuBackgroundColor || '#ffffff');
      setMenuButtonColor(menuData.menuButtonColor || '#004165');
      setMenuTextColor(menuData.menuTextColor || '#111111');
      emitPageBuilderUpdated();
    } catch (err) {
      setError(err.message);
      console.error('Error removing menu item:', err);
    }
  };

  const moveMenuItem = async (pageId, direction) => {
    try {
      const res = await fetch(`${API_BASE}/pagebuilder/menu/items/${pageId}/reorder`, {
        method: 'PATCH',
        headers: authHeaders,
        body: JSON.stringify({ direction: direction === 1 ? 'down' : 'up' })
      });
      if (!res.ok) throw new Error('Failed to move menu item');
      const menuData = await res.json();
      const items = menuData.items || [];
      setMenu(items);
      setMenuBackgroundColor(menuData.menuBackgroundColor || '#ffffff');
      setMenuButtonColor(menuData.menuButtonColor || '#004165');
      setMenuTextColor(menuData.menuTextColor || '#111111');
      emitPageBuilderUpdated();
    } catch (err) {
      setError(err.message);
      console.error('Error moving menu item:', err);
    }
  };

  const renameMenuItem = async (pageId, label) => {
    try {
      const res = await fetch(`${API_BASE}/pagebuilder/menu/items/${pageId}`, {
        method: 'PATCH',
        headers: authHeaders,
        body: JSON.stringify({ label })
      });
      if (!res.ok) throw new Error('Failed to rename menu item');
      const menuData = await res.json();
      const items = menuData.items || [];
      setMenu(items);
      setMenuBackgroundColor(menuData.menuBackgroundColor || '#ffffff');
      setMenuButtonColor(menuData.menuButtonColor || '#004165');
      setMenuTextColor(menuData.menuTextColor || '#111111');
      emitPageBuilderUpdated();
    } catch (err) {
      setError(err.message);
      console.error('Error renaming menu item:', err);
    }
  };

  const setMenuItemParent = async (pageId, parentPageId) => {
    try {
      const res = await fetch(`${API_BASE}/pagebuilder/menu/items/${pageId}`, {
        method: 'PATCH',
        headers: authHeaders,
        body: JSON.stringify({ parentPageId })
      });
      if (!res.ok) throw new Error('Failed to update menu parent');
      const menuData = await res.json();
      const items = menuData.items || [];
      setMenu(items);
      setMenuBackgroundColor(menuData.menuBackgroundColor || '#ffffff');
      setMenuButtonColor(menuData.menuButtonColor || '#004165');
      setMenuTextColor(menuData.menuTextColor || '#111111');
      emitPageBuilderUpdated();
    } catch (err) {
      setError(err.message);
      console.error('Error setting menu parent:', err);
    }
  };

  const setMenuStyleColor = async (style) => {
    try {
      const res = await fetch(`${API_BASE}/pagebuilder/menu/style`, {
        method: 'PATCH',
        headers: authHeaders,
        body: JSON.stringify(style)
      });
      if (!res.ok) throw new Error('Failed to update menu style');
      const menuData = await res.json();
      setMenu(menuData.items || []);
      setMenuBackgroundColor(menuData.menuBackgroundColor || style.menuBackgroundColor || '#ffffff');
      setMenuButtonColor(menuData.menuButtonColor || style.menuButtonColor || '#004165');
      setMenuTextColor(menuData.menuTextColor || style.menuTextColor || '#111111');
      emitPageBuilderUpdated();
    } catch (err) {
      setError(err.message);
      console.error('Error updating menu style:', err);
    }
  };

  return (
    <div className="pagebuilder-root">
      {/* Loading state */}
      {loading && (
        <div className="pagebuilder-loading">
          <AppLoader lines={4} label="Loading page builder..." />
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="pagebuilder-error">
          <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />
          <p>{error}</p>
          <button
            type="button"
            className="pagebuilder-error-dismiss"
            onClick={() => setError(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Content (only when not loading) */}
      {!loading && (
        <>
          {/* Tab bar */}
          <div className="pagebuilder-tabs">
            <button
              type="button"
              className={`pagebuilder-tab${activeTab === 'pages' ? ' pagebuilder-tab--active' : ''}`}
              onClick={() => setActiveTab('pages')}
            >
              <i className="bi bi-file-earmark-text" aria-hidden="true" />
              Pages <span className="pagebuilder-block-count">{pages.length}</span>
            </button>
            <button
              type="button"
              className={`pagebuilder-tab${activeTab === 'menu' ? ' pagebuilder-tab--active' : ''}`}
              onClick={() => setActiveTab('menu')}
            >
              <i className="bi bi-list-ul" aria-hidden="true" />
              Menu <span className="pagebuilder-block-count">{menu.length}</span>
            </button>
            <button
              type="button"
              className={`pagebuilder-tab${activeTab === 'blocktypes' ? ' pagebuilder-tab--active' : ''}`}
              onClick={() => setActiveTab('blocktypes')}
            >
              <i className="bi bi-sliders" aria-hidden="true" />
              Block Types
            </button>
          </div>

          {/* ── Pages tab ─────────────────────────────────────────────── */}
          {activeTab === 'pages' && (
            <>
              <PageList
                pages={pages}
                activePageId={activePageId}
                onSelect={(id) => {
                  setActivePageId(id);
                  setShowAddPanel(false);
                  setExpandedBlockId(null);
                }}
                onAdd={addPage}
                onRename={renamePage}
                onRemove={removePage}
              />

              {activePage && (
                <>
                  <div className="pagebuilder-toolbar">
                    <span className="pagebuilder-toolbar-label">
                      <i className="bi bi-grid-1x2-fill" aria-hidden="true" />
                      {activePage.title}
                      <span className="pagebuilder-block-count">
                        {activePage.blocks.length} block{activePage.blocks.length !== 1 ? 's' : ''}
                      </span>
                    </span>
                    <button
                      type="button"
                      className="pagebuilder-add-btn"
                      onClick={() => setShowAddPanel((v) => !v)}
                    >
                      <i className={`bi ${showAddPanel ? 'bi-x-lg' : 'bi-plus-lg'}`} aria-hidden="true" />
                      {showAddPanel ? 'Cancel' : 'Add Block'}
                    </button>
                  </div>

                  <BlockList
                    blocks={activePage.blocks}
                    onUpdate={updateBlock}
                    onRemove={removeBlock}
                    onMove={moveBlock}
                    onToggleVisibility={toggleBlockVisibility}
                    expandedBlockId={expandedBlockId}
                  />

                  {showAddPanel && (
                    <>
                      <AddBlockPanel onAdd={addBlock} enabledBlockTypes={enabledBlockTypes} />
                      <div ref={addPanelBottomRef} />
                    </>
                  )}

                  {activePage.blocks.length === 0 && !showAddPanel && (
                    <div className="pagebuilder-empty">
                      <i className="bi bi-layout-text-sidebar-reverse pagebuilder-empty-icon" aria-hidden="true" />
                      <p className="pagebuilder-empty-text">
                        No blocks yet. Click <strong>Add Block</strong> to start building this page.
                      </p>
                    </div>
                  )}
                </>
              )}

              {!activePage && pages.length > 0 && (
                <div className="pagebuilder-empty">
                  <i className="bi bi-hand-index-thumb pagebuilder-empty-icon" aria-hidden="true" />
                  <p className="pagebuilder-empty-text">
                    Select a page from the list above to start editing its blocks.
                  </p>
                </div>
              )}
            </>
          )}

          {/* ── Menu tab ──────────────────────────────────────────────── */}
          {activeTab === 'menu' && (
            <MenuEditor
              pages={pages}
              menu={menu}
              menuBackgroundColor={menuBackgroundColor}
              menuButtonColor={menuButtonColor}
              menuTextColor={menuTextColor}
              onAdd={addToMenu}
              onRemove={removeFromMenu}
              onMove={moveMenuItem}
              onRename={renameMenuItem}
              onSetParent={setMenuItemParent}
              onSetStyle={setMenuStyleColor}
            />
          )}

          {/* ── Block Types tab ────────────────────────────────────────── */}
          {activeTab === 'blocktypes' && (
            <BlockTypesConfig
              authToken={token}
              onConfigChanged={(enabled) => setEnabledBlockTypes(new Set(enabled))}
            />
          )}

        </>
      )}
    </div>
  );
}
